function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function buildContext(fieldLike = {}) {
  const sourceLabel = normalizeText(fieldLike.sourceLabel || fieldLike.name || "");
  const plainEnglishLabel = normalizeText(fieldLike.plainEnglishLabel || "");
  const section = normalizeText(fieldLike.section || "");
  const combined = `${sourceLabel} ${plainEnglishLabel} ${section}`.trim();
  return {
    sourceLabel,
    plainEnglishLabel,
    section,
    combined,
  };
}

const NON_PROTECTED_PATTERNS = [
  /\bcrew\b/,
  /\bcrew member\b/,
  /\bphysician\b/,
  /\bdoctor\b/,
  /\battending\b/,
  /\bprovider\b/,
];

export const CANDIDATE_PHI_FIELD_POLICIES = [
  {
    key: "patient_name",
    label: "Patient name",
    protectByDefault: true,
    patterns: [
      /\bpatient('?s)? last name\b/,
      /\bpatient('?s)? first name\b/,
      /\bpatient('?s)? middle name\b/,
      /\blast name\b/,
      /\bfirst name\b/,
      /\bmiddle name\b/,
      /\bmaiden name\b/,
      /\bpatient name\b/,
      /\bpt name\b/,
    ],
  },
  {
    key: "date_of_birth",
    label: "DOB",
    protectByDefault: true,
    patterns: [
      /\bdob\b/,
      /\bdate of birth\b/,
      /\bbirth date\b/,
    ],
  },
  {
    key: "address",
    label: "Address",
    protectByDefault: true,
    patterns: [
      /\bpatient('?s)? address\b/,
      /\bmailing address\b/,
      /\bhome address\b/,
      /\bstreet address\b/,
      /\baddress 2\b/,
      /\bapartment\b/,
      /\bsuite\b/,
    ],
  },
  {
    key: "contact",
    label: "Contact info",
    protectByDefault: true,
    patterns: [
      /\bpatient('?s)? telephone\b/,
      /\bpatient('?s)? phone\b/,
      /\btelephone number\b/,
      /\bphone number\b/,
      /\bmobile\b/,
      /\bcell\b/,
      /\be-?mail\b/,
    ],
  },
  {
    key: "record_identifier",
    label: "Record ID",
    protectByDefault: true,
    patterns: [
      /\bmrn\b/,
      /\bpatient id\b/,
      /\baccount number\b/,
      /\bencounter number\b/,
      /\bvisit number\b/,
      /\bfin\b/,
      /\bsubscriber\b/,
      /\bsocial security\b/,
      /\bssn\b/,
      /\bpolicy number\b/,
    ],
  },
  {
    key: "signature",
    label: "Signature",
    protectByDefault: true,
    patterns: [
      /\bsignature\b/,
      /\bauthorized rep\b/,
      /\bpatient\/authorized rep\b/,
    ],
  },
  {
    key: "patient_demographics",
    label: "Patient demographic",
    protectByDefault: false,
    patterns: [
      /\bage\b/,
      /\bgender\b/,
      /\bsex\b/,
      /\bethnicity\b/,
      /\brace\b/,
      /\bcountry\b/,
      /\bnationality\b/,
      /\blanguage\b/,
    ],
  },
];

export const CANDIDATE_PHI_FIELD_NAMES = CANDIDATE_PHI_FIELD_POLICIES.flatMap((policy) =>
  policy.patterns.map((pattern) => pattern.toString())
);

export function matchProtectedFieldPolicy(fieldLike = {}) {
  const context = buildContext(fieldLike);
  if (!context.combined) return null;
  if (NON_PROTECTED_PATTERNS.some((pattern) => pattern.test(context.combined))) {
    return null;
  }

  for (const policy of CANDIDATE_PHI_FIELD_POLICIES) {
    if (policy.patterns.some((pattern) => pattern.test(context.combined))) {
      return {
        key: policy.key,
        label: policy.label,
        protectByDefault: policy.protectByDefault,
      };
    }
  }

  return null;
}

export function applyProtectedFieldPolicyToField(field = {}) {
  const policy = matchProtectedFieldPolicy(field);
  const override = String(field?.protectionOverride || "").trim().toLowerCase();
  const templateRedacted = Boolean(field?.templateRedacted);

  if (override === "unprotected") {
    return {
      ...field,
      isProtected: false,
      protectionReason: "",
      protectionHintType: policy?.key || "",
    };
  }

  if (override === "protected") {
    return {
      ...field,
      isProtected: true,
      protectionReason: field.protectionReason || "System-admin protected",
      protectionHintType: policy?.key || "",
    };
  }

  if (templateRedacted) {
    return {
      ...field,
      isProtected: true,
      protectionReason: field.protectionReason || field.templateRedactionReason || "Protected by template redaction",
      protectionHintType: policy?.key || "template_redacted",
    };
  }

  if (policy?.protectByDefault) {
    return {
      ...field,
      isProtected: true,
      protectionReason: field.protectionReason || `Protected by policy: ${policy.key}`,
      protectionHintType: policy.key,
    };
  }

  return {
    ...field,
    isProtected: Boolean(field.isProtected),
    protectionReason: field.protectionReason || "",
    protectionHintType: policy?.key || "",
  };
}

export function applyProtectedFieldPolicyToFields(fields = []) {
  return (Array.isArray(fields) ? fields : []).map((field) => applyProtectedFieldPolicyToField(field));
}
